import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Chip,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';
import { useOrgCuratedCurrencies } from '../hooks/useOrgCuratedCurrencies';
import { formatCurrencyPickerLabel } from '../utils/iso4217';
import { formatDecimalFixed } from '../utils/timesheetEntryDisplay';
import TaskBudgetSliceCard from './TaskBudgetSliceCard';
import {
  TaskDashboardProvider,
  useTaskDashboardOptional
} from './taskDashboard/TaskDashboardContext';
import TaskDashboardSection from './taskDashboard/TaskDashboardSection';
import TaskDashboardControls from './taskDashboard/TaskDashboardControls';
import TaskContributorsTable from './taskDashboard/TaskContributorsTable';
import TaskTimesheetSection from './taskDashboard/TaskTimesheetSection';

interface Task {
  id: string;
  name: string;
  accountId: string;
  organizationId: string;
  taskNumber?: string;
  description?: string;
  isBillable: boolean;
  billingRate?: number;
  currency?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TaskFormData {
  name: string;
  taskNumber: string;
  description: string;
  isBillable: boolean;
  billingRate: string;
  currency: string;
  isActive: boolean;
}

interface TaskViewProps {
  taskId: string;
  taskName: string;
  accountId: string;
  organizationId: string;
  onBack: () => void;
  userRole?: string;
}

function formatTaskHeaderLabel(task: Task): string {
  return task.taskNumber ? `${task.taskNumber} - ${task.name}` : task.name;
}

function TaskViewBody({
  task,
  accountId,
  organizationId,
  canEdit,
  error,
  setError,
  editDialogOpen,
  setEditDialogOpen,
  formData,
  setFormData,
  curatedCodes,
  onSave,
  onDelete,
  onBack
}: {
  task: Task;
  accountId: string;
  organizationId: string;
  canEdit: boolean;
  error: string | null;
  setError: (v: string | null) => void;
  editDialogOpen: boolean;
  setEditDialogOpen: (v: boolean) => void;
  formData: TaskFormData;
  setFormData: React.Dispatch<React.SetStateAction<TaskFormData>>;
  curatedCodes: string[];
  onSave: () => void;
  onDelete: () => void;
  onBack: () => void;
}) {
  const { formatDateOnly } = useDateFormat();
  const dashboard = useTaskDashboardOptional();
  const economyVisible = dashboard?.accessGranted === true;

  const handleTargetSaved = () => {
    dashboard?.refreshBudgetConfig();
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{formatTaskHeaderLabel(task)}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={onDelete}
              >
                Delete
              </Button>
            </>
          )}
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TaskDashboardControls />

      <TaskDashboardSection />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <TaskContributorsTable accountId={accountId} economyVisible={economyVisible} />
              </CardContent>
            </Card>
            <TaskTimesheetSection taskId={task.id} organizationId={organizationId} />
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Task Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Properties
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip
                        label={task.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={task.isActive ? 'success' : 'default'}
                      />
                      {task.isBillable && (
                        <Chip label="Billable" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                  {task.description && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1">{task.description}</Typography>
                    </Box>
                  )}
                  {formatDecimalFixed(task.billingRate, 2) != null && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Billing Rate
                      </Typography>
                      <Typography variant="body1">
                        {task.currency || 'EUR'} {formatDecimalFixed(task.billingRate, 2)}/hour
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body1">{formatDateOnly(task.createdAt)}</Typography>
                  </Box>
                  {task.updatedAt !== task.createdAt && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body1">{formatDateOnly(task.updatedAt)}</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            <TaskBudgetSliceCard
              accountId={accountId}
              taskId={task.id}
              canEdit={canEdit}
              economyVisible={economyVisible}
              onTargetSaved={handleTargetSaved}
            />
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Task Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Task Number"
              value={formData.taskNumber}
              onChange={(e) => setFormData({ ...formData, taskNumber: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                label="Currency"
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                disabled={curatedCodes.length === 0}
              >
                {curatedCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {formatCurrencyPickerLabel(code)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Billing Rate"
              value={formData.billingRate}
              onChange={(e) => setFormData({ ...formData, billingRate: e.target.value })}
              fullWidth
              type="number"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>{formData.currency}</Typography>
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isBillable}
                  onChange={(e) => setFormData({ ...formData, isBillable: e.target.checked })}
                />
              }
              label="Billable"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={onSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function TaskView({
  taskId,
  accountId,
  organizationId,
  onBack,
  userRole = 'MEMBER'
}: TaskViewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [task, setTask] = useState<Task | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState<TaskFormData>({
    name: '',
    taskNumber: '',
    description: '',
    isBillable: true,
    billingRate: '',
    currency: 'EUR',
    isActive: true
  });

  const { curatedCodes } = useOrgCuratedCurrencies(organizationId);
  const canEdit = userRole === 'OWNER' || userRole === 'MANAGER';

  useEffect(() => {
    setTask(null);
    setLoading(true);
    loadTask();
  }, [taskId]);

  const loadTask = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getTask(taskId);
      if (response.success && response.data) {
        const taskData = response.data;
        setTask(taskData);
        setFormData({
          name: taskData.name,
          taskNumber: taskData.taskNumber || '',
          description: taskData.description || '',
          isBillable: taskData.isBillable,
          billingRate: taskData.billingRate?.toString() || '',
          currency: taskData.currency || 'EUR',
          isActive: taskData.isActive
        });
      } else {
        setError('Task not found');
      }
    } catch (err: any) {
      console.error('Error loading task:', err);
      setError(err.response?.data?.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setError(null);
      const taskData = {
        ...formData,
        accountId,
        organizationId,
        billingRate: formData.billingRate ? parseFloat(formData.billingRate) : undefined,
        currency: formData.currency
      };
      const response = await api.updateTask(taskId, taskData);
      if (response.success) {
        await loadTask();
        setEditDialogOpen(false);
      } else {
        setError(response.message || 'Failed to update task');
      }
    } catch (err: any) {
      console.error('Error updating task:', err);
      setError(err.response?.data?.message || 'Failed to update task');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      try {
        setError(null);
        await api.deleteTask(taskId);
        onBack();
      } catch (err: any) {
        console.error('Error deleting task:', err);
        setError(err.response?.data?.message || 'Failed to delete task');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading task...</Typography>
      </Box>
    );
  }

  if (error && !task) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={onBack} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  if (!task) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TaskDashboardProvider
        accountId={accountId}
        taskId={taskId}
        accountCurrency={task.currency || 'EUR'}
      >
        <TaskViewBody
          task={task}
          accountId={accountId}
          organizationId={organizationId}
          canEdit={canEdit}
          error={error}
          setError={setError}
          editDialogOpen={editDialogOpen}
          setEditDialogOpen={setEditDialogOpen}
          formData={formData}
          setFormData={setFormData}
          curatedCodes={curatedCodes}
          onSave={handleSave}
          onDelete={handleDelete}
          onBack={onBack}
        />
      </TaskDashboardProvider>
    </Box>
  );
}
